/* eslint-disable import/no-cycle */
import React, { ReactNode, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Appearance } from 'design/types';
import { selectCurrentEmployeeType } from 'store/Employers';
import { EmployeeActionRequiredType, EmployeeStatus, EmployeeType } from 'types';
import { capitalize, formatDateForUI, formatInviteRecipient, formatJobTitle, getTimeWithZone } from 'utils';
import { orgRoleDisplay } from 'utils/employee';
import { Button, ButtonAdd } from '../../Button';
import { Empty } from '../../Empty';
import { PaginationFooter } from '../../Pagination';
import { Status } from '../../Status';
import { Tooltip } from '../../Tooltip';
import { User } from '../../User';
import { ChildData } from '../ChildData';
import EmptyTable from '../EmptyTable';
import HeaderCell from '../HeaderCell';
import HeaderRow from '../HeaderRow';
import { ParentCell, ParentCellInner, ParentDataButton } from '../ParentCell';
import SortLabel from '../SortLabel';
import Table from '../Table';
import TableBody from '../TableBody';
import TableHead from '../TableHead';
import TableTimestamp from '../TableTimestamp';
import { ButtonIsLoading } from './styles';
import { UserTableDataProps } from './UserTableDataProps';
import { UserTableRow } from './UserTableRow';

interface ITableProps {
  data?: UserTableDataProps[];
  tableHeaders: TableHeaderObject[];
  isLoading?: boolean;
  onEmployeeSelect?: (employee: UserTableDataProps) => void;
  onReviewSelect?: (id: string) => void;
  onResendSelect?: (id: string, employee: any) => void;
  onAddToPayeesList?: (id: string) => void;
  onSortChange?: (value: string) => void;
  isSortedBy?: (value: string) => boolean;
  isSortDesc?: (value: string) => boolean;
  pagination?: ReactNode;
  resultsPerPage?: ReactNode;
  limit: number;
  disableReviewButton?: boolean;
}

export interface TableHeaderObject {
  align?: 'center' | 'left' | 'right';
  label: string;
  value: string;
  sortable?: boolean;
}

const EmployeeTable: React.FC<ITableProps> = ({
  data,
  tableHeaders,
  isLoading,
  onEmployeeSelect,
  onReviewSelect,
  onResendSelect,
  onAddToPayeesList,
  onSortChange,
  isSortedBy,
  isSortDesc,
  pagination: Pager,
  resultsPerPage: ResultsPerPage,
  disableReviewButton,
  limit,
}) => {
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const [childVisible, setChildVisible] = useState<string>('');

  const alignTop = childVisible ? 'top' : 'center';

  // Data
  const statusDisplay = (status: string, actionRequired?: string): string => {
    if (status === EmployeeStatus.ACTION_REQUIRED || status === EmployeeStatus.INVITED) {
      if (actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
        return 'Expired';
      }
      if (actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
        return 'Pending';
      }
      if (actionRequired === EmployeeActionRequiredType.INVITE_SEND_FAILURE) {
        return 'Invited';
      }
    }
    const newStatus = status[0].toUpperCase() + status.substring(1);
    return newStatus;
  };

  const subStatusDisplay = (status: string, actionRequired?: string): string => {
    let label = '';
    if (status === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_SEND_FAILURE) {
      label = 'Delivery Failure';
    } else if ((status === EmployeeStatus.ACTION_REQUIRED || status === EmployeeStatus.INVITED) && actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
      label = 'Account Requires Approval';
    }

    return label;
  };

  const statusColor = (status: string, actionRequired?: string): Appearance => {
    if (status === EmployeeStatus.APPROVED) {
      return Appearance.SUCCESS;
    }
    if (status === EmployeeStatus.DISMISSED) {
      return Appearance.NEUTRAL;
    }
    if (status === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
      return Appearance.DANGER;
    }
    return Appearance.WARNING;
  };

  const onMultiSiteClick = (id: string, e?: MouseEvent) => {
    e?.stopPropagation();
    setChildVisible(id === childVisible ? '' : id);
  };

  return (
    <>
      <Table scrollable sticky>
        <TableHead>
          <HeaderRow sticky>
            {tableHeaders.map(({ label, value, align, sortable }: TableHeaderObject, index: number) => (
              <HeaderCell key={index} align={align}>
                {sortable ? (
                  <SortLabel label={label} isSorted={isSortedBy && isSortedBy(value)} isSortedDesc={isSortDesc && isSortDesc(value)} onClick={() => onSortChange && onSortChange(value)} isLoading={isLoading} />
                ) : isLoading ? (
                  ''
                ) : (
                  label
                )}
              </HeaderCell>
            ))}
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading &&
            isEmpty(data) &&
            [...Array(limit)].map((e, i) => {
              const displayRow = {
                name: {
                  renderComponent: (
                    <ParentCell dataLabel="Name" verticalAlign="top">
                      <ParentCellInner>
                        <Skeleton width={100} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                sites: {
                  renderComponent: (
                    <ParentCell dataLabel="Site" verticalAlign={alignTop} minWidth={150}>
                      <ParentCellInner>
                        <Skeleton width={150} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                role: {
                  renderComponent: (
                    <ParentCell dataLabel="Role" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                jobTitle: {
                  renderComponent: (
                    <ParentCell dataLabel="Job Title" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                status: {
                  renderComponent: (
                    <ParentCell dataLabel="Status" align="left" verticalAlign="top" minWidth={121}>
                      <ParentCellInner>
                        <Skeleton width={121} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                createdAt: {
                  renderComponent: (
                    <ParentCell dataLabel="Date Added" verticalAlign="top" minWidth={110}>
                      <ParentCellInner>
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                reviewAction: {
                  renderComponent: (
                    <ParentCell dataLabel="Review" align="center" verticalAlign="top">
                      <ParentCellInner align="right">
                        {' '}
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                inviteRecipient: {
                  renderComponent: (
                    <ParentCell dataLabel="Invite method" align="left" verticalAlign="top">
                      <ParentCellInner>
                        {' '}
                        <Skeleton width={110} />
                      </ParentCellInner>
                    </ParentCell>
                  ),
                },
                addToPayeesList: {
                  renderComponent: (
                    <ParentCell dataLabel="Add to Payees List" align="center" verticalAlign="top">
                      <ParentCellInner align="right" />
                    </ParentCell>
                  ),
                },
              };

              return <UserTableRow key={i} data-cy={`user-row-${i}`} visibleColumns={tableHeaders} dataRow={displayRow} />;
            })}

          {data?.map((row: UserTableDataProps, index: number) => {
            const { username, avatarUrl, status, actionRequired, inviteRecipient, sites, createdAt, type, hasPaymentMethod } = row;
            const isOrgAdmin = type === EmployeeType.ADMIN;
            const hasEditPermission = !disableReviewButton && (!isOrgAdmin || currentEmployeeType === EmployeeType.ADMIN);
            const employeeSiteInfo = sites.reduce(
              (acc, site) => {
                if (!isOrgAdmin) {
                  acc.roles.push(orgRoleDisplay(type, site.role));
                  if (site.job) acc.jobs.push(formatJobTitle(site.job.title));
                  acc.sites.push(site.name);
                  acc.status.push(capitalize(site.status));
                }
                return acc;
              },
              { roles: [], sites: [], jobs: [], status: [] }
            );

            const displayRow = {
              name: {
                renderComponent: (
                  <ParentCell dataLabel="Name" data-cy="col-name" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)}>
                    <ParentCellInner>
                      <User id={row.id} username={username} image={avatarUrl} isLoading={isLoading} stackable={false} hasPaymentMethod={hasPaymentMethod} isDismissed={status === EmployeeStatus.DISMISSED} />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },

              sites: {
                renderComponent: (
                  <ParentCell dataLabel="Site" data-cy="col-site" verticalAlign={alignTop} onClick={() => onEmployeeSelect && onEmployeeSelect(row)} minWidth={150}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(row.id, e)} isActive={row.id === childVisible} data={isOrgAdmin ? ['All'] : employeeSiteInfo.sites} label="Sites" isLoading={isLoading} />
                    </ParentCellInner>

                    <ChildData position="start" isVisible={row.id === childVisible} data={employeeSiteInfo.sites.length === 1 ? null : employeeSiteInfo.sites} />
                  </ParentCell>
                ),
              },

              role: {
                renderComponent: (
                  <ParentCell dataLabel="Role" data-cy="col-role" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)} minWidth={121}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(row.id, e)} isActive={row.id === childVisible} data={isOrgAdmin ? [orgRoleDisplay(type)] : employeeSiteInfo.roles} label="Roles" isLoading={isLoading} />
                    </ParentCellInner>

                    <ChildData position="middle" isVisible={row.id === childVisible} data={employeeSiteInfo.roles.length === 1 ? null : employeeSiteInfo.roles} />
                  </ParentCell>
                ),
              },

              jobTitle: {
                renderComponent: (
                  <ParentCell dataLabel="Job Title" data-cy="col-job-title" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)} minWidth={121}>
                    <ParentCellInner>
                      <ParentDataButton onClick={(e) => onMultiSiteClick(row.id, e)} isActive={row.id === childVisible} data={employeeSiteInfo.jobs} label="Jobs" isLoading={isLoading} />
                    </ParentCellInner>
                    <ChildData position="middle" isVisible={row.id === childVisible} data={employeeSiteInfo.jobs.length === 1 ? null : employeeSiteInfo.jobs} />
                  </ParentCell>
                ),
              },

              status: {
                renderComponent: (
                  <ParentCell dataLabel="Status" data-cy="col-status" align="left" verticalAlign="top" minWidth={121} onClick={() => onEmployeeSelect && onEmployeeSelect(row)}>
                    <ParentCellInner>
                      <Status appearance={statusColor(status, actionRequired)} label={statusDisplay(status, actionRequired)} subStatusLabel={subStatusDisplay(status, actionRequired)} isLoading={isLoading} />
                    </ParentCellInner>
                    <ChildData
                      position="end"
                      isVisible={row.id === childVisible}
                      data={employeeSiteInfo.status.length === 1 ? null : status === EmployeeStatus.ACTION_REQUIRED ? Array(employeeSiteInfo.status.length).fill(statusDisplay(status, actionRequired)) : employeeSiteInfo.status}
                    />
                  </ParentCell>
                ),
              },
              createdAt: {
                renderComponent: (
                  <ParentCell dataLabel="Date Added" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)} minWidth={110}>
                    <ParentCellInner>
                      <TableTimestamp isLoading={isLoading} date={formatDateForUI(createdAt)} time={getTimeWithZone(createdAt)} />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },

              reviewAction: {
                renderComponent: (
                  <ParentCell dataLabel="Review" align="center" verticalAlign="top">
                    {(row.actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED || row.actionRequired === EmployeeActionRequiredType.INVITE_SEND_FAILURE) && hasEditPermission && (
                      <ParentCellInner align="right">
                        <Button
                          appearance="white"
                          size="small"
                          label="Resend"
                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            e?.stopPropagation();
                            onResendSelect?.(row.id, row);
                          }}
                          isLoading={isLoading}
                          disabled={isLoading}
                        />
                      </ParentCellInner>
                    )}

                    {row.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL && (
                      <ParentCellInner align="right">
                        <Button
                          appearance="primary"
                          size="small"
                          label="Review"
                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            e?.stopPropagation();
                            onReviewSelect?.(row.id);
                          }}
                          isLoading={isLoading}
                          disabled={isLoading || !hasEditPermission}
                          tooltipId={`${row.id}-manager-disabled`}
                        />
                        {!hasEditPermission && (
                          <Tooltip id={`${row.id}-manager-disabled`} place="left">
                            You do not have permission to review this user.
                            <br />
                            Contact your manager.
                          </Tooltip>
                        )}
                      </ParentCellInner>
                    )}
                  </ParentCell>
                ),
              },

              inviteRecipient: {
                renderComponent: (
                  <ParentCell dataLabel="Invite method" align="left" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)}>
                    <ParentCellInner>
                      <ParentDataButton data={[formatInviteRecipient(inviteRecipient) || '']} label="Invite Recipient" onClick={() => onEmployeeSelect && onEmployeeSelect(row)} isLoading={isLoading} />
                    </ParentCellInner>
                  </ParentCell>
                ),
              },

              addToPayeesList: {
                renderComponent: (
                  <ParentCell dataLabel="Add to Payees List" align="center" verticalAlign="top" onClick={() => onEmployeeSelect && onEmployeeSelect(row)}>
                    <ParentCellInner align="right">
                      {isLoading ? (
                        <ButtonIsLoading />
                      ) : (
                        <ButtonAdd
                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            e?.stopPropagation();
                            onAddToPayeesList?.(row.id);
                          }}
                          disabled={isLoading}
                          isAdded={row.isAdded}
                        >
                          {row.isAdded ? 'Added' : 'Add'}
                        </ButtonAdd>
                      )}
                    </ParentCellInner>
                  </ParentCell>
                ),
              },
            };

            return <UserTableRow key={index} data-cy={`user-row-${index}`} visibleColumns={tableHeaders} dataRow={displayRow} />;
          })}

          {!isLoading && isEmpty(data) && (
            <EmptyTable>
              <Empty icon="People" message="No users found." />
            </EmptyTable>
          )}
        </TableBody>
      </Table>
      <PaginationFooter>
        {!isLoading && (
          <>
            {ResultsPerPage}
            {Pager}
          </>
        )}
      </PaginationFooter>
    </>
  );
};

export default EmployeeTable;
